import { Carousel } from '@mantine/carousel';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useFetchCommunityFeedMemories from 'src/Hooks/Queries/useFetchCommunityFeedMemories';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import { CreateMemoryButton } from './CreateMemoryButton';
import styles from './Memories.module.scss';
import { Memory } from './Memory';

/**
 * The list of memories rendered at the top of the community screen.
 */
const Memories = () => {
  const {
    data: memories,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useFetchCommunityFeedMemories();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();

  const [memoryToOpenId, setMemoryToOpenId] = useState<string | null>(null);

  const memoriesJSX =
    memories?.pages.flatMap(page =>
      page.items.flatMap(memory => {
        const onOpen = () => {
          if (memory.id === memoryToOpenId) {
            setMemoryToOpenId(null);
          }
        };

        return (
          <Carousel.Slide key={memory.id}>
            <Memory
              memory={memory}
              shouldOpenModal={memory.id === memoryToOpenId}
              onModalOpened={onOpen}
            />
          </Carousel.Slide>
        );
      })
    ) ?? [];

  const onSlideChange = (index: number) => {
    // If there is less than 10 memories left, fetch the next memories
    if (index > Math.max(memoriesJSX.length - 10, 0)) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div>
      <Carousel
        height={99}
        slideSize={132}
        slidesToScroll={1}
        slideGap="8"
        align="start"
        dragFree={false}
        skipSnaps
        withControls={(memoriesJSX?.length ?? 0) > 3}
        nextControlIcon={<IconChevronRight size={18} />}
        previousControlIcon={<IconChevronLeft size={18} />}
        onSlideChange={onSlideChange}
        classNames={{ control: styles.control }}
      >
        <Carousel.Slide>
          <CreateMemoryButton
            onClick={() => navigate(`/asset/${assetId}/memories/new?source=community`)}
          />
        </Carousel.Slide>

        {memoriesJSX}
      </Carousel>
    </div>
  );
};

export default Memories;
